import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useProfileStore } from '@/store'
import type {
	ApiResponse,
	CartaoAmareloAllListOutput,
	CartaoAmareloAllListParams,
	CartaoAmareloCreateInput,
	CartaoAmareloListOutput,
	CartaoAmareloListParams,
	CartaoAmareloMeListOutput,
	CartaoAmareloMeListParams,
	CartaoAmareloUpdateInput,
} from '@/types'
import {
	createCartaoAmarelo,
	deleteCartaoAmarelo,
	getCartaoAmareloAllList,
	getCartaoAmareloList,
	getCartaoAmareloMeList,
	updateCartaoAmarelo,
} from '../services/cartao-amarelo'

const QUERY_KEY = ['cartao-amarelo', 'list']
const ME_QUERY_KEY = ['cartao-amarelo', 'me', 'list']

export function useCartaoAmareloList(params?: CartaoAmareloListParams) {
	return useQuery<
		ApiResponse<CartaoAmareloListOutput>,
		Error,
		CartaoAmareloListOutput
	>({
		queryKey: [...QUERY_KEY, params],
		queryFn: () => getCartaoAmareloList(params),
		select: (data) => data.data as CartaoAmareloListOutput,
		staleTime: 5 * 60 * 1000,
	})
}

export function useCartaoAmareloMeList(params?: CartaoAmareloMeListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: CartaoAmareloMeListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<
		ApiResponse<CartaoAmareloMeListOutput>,
		Error,
		CartaoAmareloMeListOutput
	>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getCartaoAmareloMeList(scopedParams),
		select: (data) => data.data as CartaoAmareloMeListOutput,
		staleTime: 5 * 60 * 1000,
	})
}

export function useCartaoAmareloAllList(params?: CartaoAmareloAllListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: CartaoAmareloAllListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<
		ApiResponse<CartaoAmareloAllListOutput>,
		Error,
		CartaoAmareloAllListOutput
	>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getCartaoAmareloAllList(scopedParams),
		select: (data) => data.data as CartaoAmareloAllListOutput,
		staleTime: 5 * 60 * 1000,
	})
}

export function useCreateCartaoAmarelo() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (input: CartaoAmareloCreateInput) => createCartaoAmarelo(input),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
		},
	})
}

export function useUpdateCartaoAmarelo() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string
			data: CartaoAmareloUpdateInput
		}) => updateCartaoAmarelo(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
		},
	})
}

export function useDeleteCartaoAmarelo() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: deleteCartaoAmarelo,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
		},
	})
}
